// src/components/auth/Register.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SparkLayout   from '../layouts/SparkLayout';
import SparkCard     from '../common/SparkCard';
import SparkButton   from '../common/SparkButton';
import SparkInput    from '../common/SparkInput';
import SparkHeading  from '../common/SparkHeading';
import SparkFooter   from '../common/SparkFooter';

const Register: React.FC = () => {
  const navigate            = useNavigate();
  const { register, loading } = useAuth();

  const [displayName, setDisplayName] = useState('');
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [error,       setError]       = useState<string | null>(null);
  const [success,     setSuccess]     = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      await register(email, password, displayName);
      setSuccess('Registration successful! Please check your email to verify your account.');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to register.');
    }
  };

  if (loading) {
    return (
      <SparkLayout>
        <SparkCard>
          <div className="loading-container flex items-center justify-center h-40">
            <div className="text-center">
              <div className="spark-spinner mx-auto mb-4" />
              <p className="text-xl">Loading...</p>
            </div>
          </div>
        </SparkCard>
      </SparkLayout>
    );
  }

  return (
    <SparkLayout>

      {/*  -----------------------------------------------
          Local, component‑scoped CSS override
          ----------------------------------------------- */}
      <style>{`
        .legal-link,
        .legal-link:visited {
          color: rgba(255, 255, 255, 0.60) !important;   /* soft white */
          text-decoration: none !important;              /* kill underline */
        }
        .legal-link:hover {
          color: rgba(255, 255, 255, 0.90) !important;   /* brighten on hover */
        }
      `}</style>

      <SparkCard className="max-w-md mx-auto">
        <SparkHeading>Create Account</SparkHeading>

        {error && (
          <div className="spark-message spark-message--error mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="spark-message spark-message--success mb-6">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <SparkInput
            label="Display Name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter your name"
            required
          />

          <SparkInput
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
          />

          <SparkInput
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Create a password"
            required
          />

          <SparkFooter>
            <SparkButton
              type="submit"
              fullWidth
              disabled={
                loading ||
                !displayName.trim() ||
                !email.trim() ||
                !password.trim()
              }
            >
              {loading ? 'CREATING ACCOUNT...' : 'SIGN UP'}
            </SparkButton>
          </SparkFooter>
        </form>

        <div className="mt-8 text-center">
          <div className="text-white/80">
            Already have an account?{' '}
            <SparkButton
              variant="secondary"
              to="/login"
              className="text-sm inline-block ml-2"
            >
              LOGIN HERE
            </SparkButton>
          </div>
        </div>

        {/* Legal links */}
        <div className="mt-6 text-center text-xs">
          By signing up, you agree to our{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="legal-link"
          >
            Terms&nbsp;and&nbsp;Conditions
          </a>{' '}
          and{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="legal-link"
          >
            Privacy&nbsp;Policy
          </a>.
        </div>
      </SparkCard>
    </SparkLayout>
  );
};

export default Register;
